/*
  Free gifts: keeps exactly one of each gift variant (window.asdFreeGifts.variantIds) for every regular piece in
  the cart - 1 piece = 1 box + 1 certificate, 3 pieces = 3 + 3 - and removes them when there are no pieces.
  Gifts are real products made free by an automatic discount in Shopify admin, so checkout, the order and the
  customer account show them with their original price crossed out.

  Every cart change triggers a full re-count from scratch (changes made while a sync is running are queued, never
  dropped). Each gift is kept on a single cart line; extra/split lines are merged away.
  Safety: a gift unit that is not free (discount not covering it) is removed, so a customer is never charged.
*/
(function () {
  const config = window.asdFreeGifts;
  if (!config || !config.enabled || !config.variantIds || !config.variantIds.length) return;

  const giftIds = config.variantIds.map(String);
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const CART_URL = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const MAX_SETS = 20;
  let running = false;
  let again = false;
  let timer = null;

  function isGift(item) {
    return giftIds.includes(String(item.variant_id));
  }

  function isRegular(item) {
    return !isGift(item) && item.original_price > 0;
  }

  function getCart() {
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
  }

  function update(updates) {
    return fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Asd-Gifts': '1' },
      body: JSON.stringify({ updates: updates }),
    }).then((r) => (r.ok ? r.json() : null));
  }

  // Updates keyed by line key: each gift ends up on ONE line with the wanted quantity, all other lines of it at 0.
  function giftUpdates(cart, wantedFor) {
    const updates = {};
    let changed = false;
    giftIds.forEach((id) => {
      const lines = cart.items.filter((item) => String(item.variant_id) === id);
      const wanted = wantedFor(id, lines);
      const total = lines.reduce((sum, item) => sum + item.quantity, 0);
      if (lines.length <= 1 && total === wanted) return;
      changed = true;
      if (!lines.length) {
        updates[id] = wanted; // not in cart yet: add by variant id
        return;
      }
      lines.forEach((item, i) => (updates[item.key] = i === 0 ? wanted : 0));
    });
    return changed ? updates : null;
  }

  async function syncOnce() {
    let cart = await getCart();
    const pieces = Math.min(cart.items.filter(isRegular).reduce((sum, item) => sum + item.quantity, 0), MAX_SETS);

    const updates = giftUpdates(cart, () => pieces);
    if (!updates) return false;
    cart = await update(updates);
    if (!cart) return true;

    // Safety: keep only the gift units that are really free.
    const fix = giftUpdates(cart, (id, lines) => {
      const free = lines.reduce((sum, item) => {
        if (item.original_price <= 0) return sum + item.quantity;
        return sum + Math.floor((item.original_line_price - item.final_line_price) / item.original_price);
      }, 0);
      return Math.min(pieces, free);
    });
    if (fix) {
      await update(fix);
      console.warn('[free gifts] some gifts were not free and were removed - check the automatic discount covers every product.');
    }
    return true;
  }

  async function sync() {
    if (running) {
      again = true; // a change happened during the sync: run once more when done
      return;
    }
    running = true;
    let changed = false;
    try {
      do {
        again = false;
        if (await syncOnce()) changed = true;
      } while (again);
    } catch (e) {
      console.warn('[free gifts]', e);
    } finally {
      running = false;
    }
    if (changed) refreshCartUI();
  }

  function refreshCartUI() {
    if (window.location.pathname.replace(root, '/').startsWith('/cart')) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(new CustomEvent('cart-drawer:refresh'));
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 300);
  }

  // Watch every cart change made by the theme or apps (add to cart, quantity, remove, upsells).
  const originalFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : input && input.url;
    const headers = (init && init.headers) || {};
    const ours = headers['X-Asd-Gifts'] || (headers.get && headers.get('X-Asd-Gifts'));
    const promise = originalFetch.apply(this, arguments);
    if (url && CART_URL.test(url) && !ours) {
      promise.then(schedule, schedule);
    }
    return promise;
  };

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, schedule);
  }

  // Also check on every page load and when coming back to the tab (another tab may have changed the cart).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule();
  });
  window.addEventListener('pageshow', schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
